import React, { useState, useEffect } from 'react'
import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Permission } from '../types'
import Logo from './Logo'
import Icon from '../views/components/Icon'

interface LayoutProps {
  children: React.ReactNode
}

interface SubMenuItem {
  name: string
  href: string
  requiredPermission?: Permission
  activeMatchers?: string[]
}

interface NavigationItem {
  name: string
  href?: string
  subItems?: SubMenuItem[]
  requiredPermission?: Permission
  activeMatchers?: string[]
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [expandedSections, setExpandedSections] = useState<{
    [key: string]: boolean
  }>({
    'Manage System Data': location.pathname.startsWith('/manage'),
    Accomodations: location.pathname.startsWith('/accommodations'),
    Goals: location.pathname.startsWith('/goals'),
    'Internal Use Only': location.pathname.startsWith('/manage/organizations'),
  })
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  // Keep sections expanded when navigating within their routes
  useEffect(() => {
    setExpandedSections((prev) => ({
      ...prev,
      'Manage System Data': location.pathname.startsWith('/manage'),
      Accomodations: location.pathname.startsWith('/accommodations'),
      Goals: location.pathname.startsWith('/goals'),
      'Internal Use Only': location.pathname.startsWith(
        '/manage/organizations',
      ),
    }))
    // Close mobile menu when navigating
    setIsMobileMenuOpen(false)
  }, [location.pathname])

  // Helper function to check if user has permission
  const hasPermission = (requiredPermission?: Permission): boolean => {
    if (!requiredPermission) return true
    return user?.permissions?.includes(requiredPermission) || false
  }

  const navigation: NavigationItem[] = [
    {
      name: 'My Classes',
      href: '/user/classes',
      activeMatchers: ['/user/classes', '/user/class/:classId'],
    },
    {
      name: 'Accomodations',
      subItems: [
        {
          name: 'Raw Event Logs',
          href: '/accommodations/raw-event-logs',
          requiredPermission: Permission.VIEW_REPORTS,
        },
        {
          name: 'Accommodation Usage',
          href: '/accommodations/usage',
          requiredPermission: Permission.VIEW_REPORTS,
        },
      ],
    },
    {
      name: 'Goals',
      subItems: [
        {
          name: 'Student Goals',
          href: '/goals/students',
          activeMatchers: [
            '/goals/students',
            '/goals/students/:studentId',
            '/goals/:goalId/goal-events',
            '/goals/:goalId/goal-events/new',
            '/goals/:goalId/goal-events/:goalEventId',
          ],
        },
        {
          name: 'Raw Progress Report Logs',
          href: '/goals/raw-goal-event-logs',
          requiredPermission: Permission.VIEW_REPORTS,
          activeMatchers: ['/goals/raw-goal-event-logs'],
        },
      ],
    },
    {
      name: 'Manage System Data',
      subItems: [
        {
          name: 'Schools',
          href: '/manage/schools',
          requiredPermission: Permission.MANAGE_SCHOOLS,
          activeMatchers: ['/manage/schools', '/manage/schools/:id'],
        },
        {
          name: 'Users',
          href: '/manage/users',
          requiredPermission: Permission.MANAGE_USERS,
          activeMatchers: ['/manage/users', '/manage/users/:id'],
        },
        {
          name: 'Classes',
          href: '/manage/classes',
          requiredPermission: Permission.MANAGE_CLASSES,
          activeMatchers: ['/manage/classes', '/manage/classes/:id'],
        },
        {
          name: 'Students',
          href: '/manage/students',
          requiredPermission: Permission.MANAGE_STUDENTS,
          activeMatchers: [
            '/manage/students',
            '/manage/students/:id',
            '/manage/students/:studentId/general',
            '/manage/students/:studentId/accommodations',
            '/manage/students/:studentId/goals',
            '/manage/students/:studentId/goals/new',
            '/manage/students/:studentId/goals/:goalId',
          ],
        },
        {
          name: 'Import Student Data',
          href: '/manage/import',
          requiredPermission: Permission.MANAGE_STUDENTS,
          activeMatchers: ['/manage/import'],
        },
      ],
    },
  ]

  // Filter navigation items based on permissions
  const getFilteredNavigation = (): NavigationItem[] => {
    return navigation.flatMap((item) => {
      // Check if user has permission for main nav item
      if (!hasPermission(item.requiredPermission)) {
        return []
      }

      // If item has subItems, filter them too
      if (item.subItems) {
        const filteredSubItems = item.subItems.filter((subItem) =>
          hasPermission(subItem.requiredPermission),
        )

        // If no sub-items are accessible, hide the parent item
        if (filteredSubItems.length === 0) {
          return []
        }

        return [{ ...item, subItems: filteredSubItems }]
      }

      return [item]
    })
  }

  const filteredNavigation = getFilteredNavigation()

  const toggleSection = (sectionName: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionName]: !prev[sectionName],
    }))

    // If clicking on a section and it's not expanded, navigate to the default sub-item
    if (!expandedSections[sectionName]) {
      const section = filteredNavigation.find(
        (item) => item.name === sectionName,
      )
      if (section?.subItems?.[0]) {
        navigate(section.subItems[0].href)
      }
    }
  }

  const isPathMatch = (href: string, activeMatchers?: string[]): boolean => {
    const patterns =
      activeMatchers && activeMatchers.length > 0 ? activeMatchers : [href]

    return patterns.some((pattern) =>
      Boolean(matchPath({ path: pattern, end: true }, location.pathname)),
    )
  }

  const isActiveSection = (item: NavigationItem): boolean => {
    if (item.href) {
      return isPathMatch(item.href, item.activeMatchers)
    }
    if (item.subItems) {
      return item.subItems.some((subItem) =>
        isPathMatch(subItem.href, subItem.activeMatchers),
      )
    }
    return false
  }

  const isActiveSubItem = (subItem: SubMenuItem): boolean => {
    return isPathMatch(subItem.href, subItem.activeMatchers)
  }

  const getCurrentPageName = (): string => {
    // First check for direct matches
    const directMatch = filteredNavigation.find(
      (item) => item.href && isPathMatch(item.href, item.activeMatchers),
    )
    if (directMatch) {
      return directMatch.name
    }

    // Then check sub-items
    for (const item of filteredNavigation) {
      if (item.subItems) {
        const subMatch = item.subItems.find((subItem) =>
          isPathMatch(subItem.href, subItem.activeMatchers),
        )
        if (subMatch) {
          return subMatch.name
        }
      }
    }

    return 'Page'
  }

  return (
    <div className="h-screen bg-gray-50 flex">
      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white shadow-lg flex flex-col flex-shrink-0 transition-transform duration-300 ease-in-out`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 border-b border-gray-200 px-4">
            <Logo />
            {/* Mobile close button */}
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="lg:hidden p-2 text-gray-400 hover:text-gray-600"
            >
              <Icon type="x" className="w-6 h-6" />
            </button>
          </div>
          <div className="bg-blue-50 flex items-center justify-between px-4 py-3">
            <h3>
              {typeof user?.organization === 'string'
                ? user.organization
                : user?.organization?.name}
            </h3>
          </div>
          {/* Navigation */}
          <nav className="flex-1 px-4 py-3 space-y-1 overflow-y-auto">
            {filteredNavigation.map((item) => (
              <div key={item.name}>
                {/* Main navigation item */}
                {item.href ? (
                  /* Regular link */
                  <Link
                    to={item.href}
                    className={`${
                      isActiveSection(item)
                        ? 'bg-blue-50 border-blue-500 text-blue-700'
                        : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    } group flex items-center px-3 py-2 text-sm font-medium border-l-4 rounded-r-md transition-colors duration-200`}
                  >
                    {item.name}
                  </Link>
                ) : (
                  /* Collapsible section */
                  <button
                    onClick={() => toggleSection(item.name)}
                    className={`${
                      isActiveSection(item)
                        ? 'bg-blue-50 border-blue-500 text-blue-700'
                        : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    } group flex items-center justify-between w-full px-3 py-2 text-sm font-medium border-l-4 rounded-r-md transition-colors duration-200`}
                  >
                    <span>{item.name}</span>
                    <Icon
                      type="chevron-right"
                      className={`${
                        expandedSections[item.name] ? 'rotate-90' : ''
                      } w-4 h-4 transition-transform duration-200`}
                    />
                  </button>
                )}

                {/* Sub-navigation items */}
                {item.subItems && expandedSections[item.name] && (
                  <div className="ml-4 mt-1 space-y-1">
                    {item.subItems.map((subItem) => (
                      <Link
                        key={subItem.name}
                        to={subItem.href}
                        className={`${
                          isActiveSubItem(subItem)
                            ? 'bg-blue-50 border-blue-400 text-blue-700'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                        } group flex items-center px-3 py-2 text-xs font-medium border-l-2 rounded-r-md transition-colors duration-200`}
                      >
                        <span className="w-2 h-2 bg-gray-300 rounded-full mr-3"></span>
                        {subItem.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* User info and logout */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex items-center mb-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {user?.firstName?.[0]}
                    {user?.lastName?.[0]}
                  </span>
                </div>
              </div>
              <div className="ml-3 min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="w-full bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0 lg:ml-0">
        {/* Header with mobile menu button */}
        <header className="bg-white shadow-sm border-b border-gray-200 flex-shrink-0">
          <div className="px-4 sm:px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Mobile menu button */}
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="lg:hidden p-2 text-gray-400 hover:text-gray-600"
              >
                <Icon type="menu" className="w-6 h-6" />
              </button>
              <h2 className="text-lg font-semibold text-gray-900 capitalize">
                {getCurrentPageName()}
              </h2>
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 p-4 sm:p-6 overflow-auto min-h-0">
          {children}
        </main>
      </div>
    </div>
  )
}

export default Layout
